import { useState, useEffect, useMemo } from 'react'
import {
	processMonthlyData,
	getDataStatistics,
	type ProcessedMonthRow,
	type DataStatistics,
} from '../utils/overdueInvoiceAging'

// Prefer /json-data/ (CI/build output); fall back to /data_models/ (backend pipeline copy)
const JSON_URLS = [
	'/json-data/overdue_invoice_aging_analysis.json',
	'/data_models/overdue_invoice_aging_analysis.json',
] as const

export interface UseOverdueInvoiceAgingResult {
	rawRows: ProcessedMonthRow[]
	processedRows: ProcessedMonthRow[]
	stats: DataStatistics | null
	loading: boolean
	error: string | null
	reload: () => void
}

export function useOverdueInvoiceAging(): UseOverdueInvoiceAgingResult {
	const [rawJson, setRawJson] = useState<unknown>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [reloadToken, setReloadToken] = useState(0)

	useEffect(() => {
		let cancelled = false
		setLoading(true)
		setError(null)
		;(async () => {
			let lastErr: unknown = null
			for (const url of JSON_URLS) {
				try {
					const r = await fetch(url)
					if (!r.ok) throw new Error(`HTTP ${r.status}`)
					const data = await r.json()
					if (!Array.isArray(data)) throw new Error('Expected JSON array')
					if (!cancelled) setRawJson(data)
					return
				} catch (e) {
					lastErr = e
				}
			}
			if (!cancelled) setError(lastErr instanceof Error ? lastErr.message : String(lastErr))
		})().finally(() => {
			if (!cancelled) setLoading(false)
		})
		return () => {
			cancelled = true
		}
	}, [reloadToken])

	const processedRows = useMemo(() => {
		if (!rawJson) return []
		try {
			return processMonthlyData(rawJson)
		} catch {
			return []
		}
	}, [rawJson])

	const stats = useMemo(() => {
		if (processedRows.length === 0) return null
		return getDataStatistics(processedRows)
	}, [processedRows])

	return {
		rawRows: processedRows,
		processedRows,
		stats,
		loading,
		error,
		reload: () => setReloadToken(t => t + 1),
	}
}
